import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Target, Sparkle, CheckCircle } from "@phosphor-icons/react";
import WatchButton from "@/components/WatchButton";
import PdfButton from "@/components/PdfButton";
import ServiceIcon from "@/components/ServiceIcon";
import CityCombobox from "@/components/CityCombobox";

const DEFAULT_WISHES = {
  type: "vendita",
  min_price: "", max_price: "",
  min_sqm: "", max_sqm: "",
  min_rooms: "", min_bathrooms: "",
  elevator: false, terrace: false, garden: false, garage: false, furnished: false,
  energy_class: "", condition: "", heating: "", exposure: "",
  min_year_built: "",
  zones: [],
  sources: [],
};

const ENERGY_CLASSES = ["A", "B", "C", "D", "E", "F", "G"];
const CONDITIONS = ["Nuovo", "Ristrutturato", "Buono stato", "Da ristrutturare"];
const HEATING = ["Autonomo gas", "Centralizzato", "Pompa di calore", "Pavimento radiante"];
const EXPOSURES = ["Nord", "Sud", "Est", "Ovest", "Doppia esposizione"];

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function RicercaIdeale() {
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [sources, setSources] = useState([]);
  const [city, setCity] = useState("milano");
  const [wishes, setWishes] = useState(DEFAULT_WISHES);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/sources").then((r) => setSources(r.data.listings || []));
  }, []);

  useEffect(() => {
    api.get(`/cities/${city}/zones`).then((r) => setZones(r.data));
    setWishes((w) => ({ ...w, zones: [] }));
  }, [city]);

  const toggleZone = (z) => setWishes((w) => ({
    ...w, zones: w.zones.includes(z) ? w.zones.filter((x) => x !== z) : [...w.zones, z],
  }));
  const toggleSource = (s) => setWishes((w) => ({
    ...w, sources: w.sources.includes(s) ? w.sources.filter((x) => x !== s) : [...w.sources, s],
  }));

  const submit = async () => {
    setLoading(true);
    const payload = {
      city,
      type: wishes.type || null,
      min_price: numOrNull(wishes.min_price),
      max_price: numOrNull(wishes.max_price),
      min_sqm: numOrNull(wishes.min_sqm),
      max_sqm: numOrNull(wishes.max_sqm),
      min_rooms: numOrNull(wishes.min_rooms),
      min_bathrooms: numOrNull(wishes.min_bathrooms),
      elevator: wishes.elevator || null,
      terrace: wishes.terrace || null,
      garden: wishes.garden || null,
      garage: wishes.garage || null,
      furnished: wishes.furnished || null,
      energy_class: wishes.energy_class || null,
      condition: wishes.condition || null,
      heating: wishes.heating || null,
      exposure: wishes.exposure || null,
      min_year_built: numOrNull(wishes.min_year_built),
      zones: wishes.zones.length ? wishes.zones : null,
      sources: wishes.sources.length ? wishes.sources : null,
    };
    // strip null values
    Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
    try {
      const { data } = await api.post("/search/ideal", { city, ...payload });
      setResults(data.results);
      setTotal(data.count);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setWishes(DEFAULT_WISHES); setResults([]); setTotal(0); };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950 flex">
      {/* Sidebar form */}
      <aside className="w-[420px] shrink-0 border-r border-white/10 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <ServiceIcon name="insights" size={44} />
            <div>
              <span className="font-mono-data text-xs text-lime-400 tracking-widest block">— RICERCA · DATA INSIGHTS</span>
              <h1 className="font-display font-black text-xl leading-tight mt-1">Descrivi l'immobile dei tuoi sogni.</h1>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
            Ogni campo è opzionale. Più criteri specifichi, più preciso sarà il match-score.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="ricerca-form">
          {/* City + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
              <div className="mt-1">
                <CityCombobox value={city} onChange={setCity} cities={cities} testId="ricerca-city" />
              </div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">TIPO</Label>
              <Select value={wishes.type} onValueChange={(v) => setWishes({ ...wishes, type: v })}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9" data-testid="ricerca-type"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="vendita">Vendita</SelectItem>
                  <SelectItem value="affitto">Affitto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Prices */}
          <div>
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">PREZZO (€)</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input placeholder="min" type="number" value={wishes.min_price} onChange={(e) => setWishes({ ...wishes, min_price: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" data-testid="ricerca-min-price" />
              <Input placeholder="max" type="number" value={wishes.max_price} onChange={(e) => setWishes({ ...wishes, max_price: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" data-testid="ricerca-max-price" />
            </div>
          </div>

          {/* Sqm + Rooms */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">SUPERFICIE (m²)</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input placeholder="min" type="number" value={wishes.min_sqm} onChange={(e) => setWishes({ ...wishes, min_sqm: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" />
                <Input placeholder="max" type="number" value={wishes.max_sqm} onChange={(e) => setWishes({ ...wishes, max_sqm: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" />
              </div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">LOCALI / BAGNI</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input placeholder="loc" type="number" value={wishes.min_rooms} onChange={(e) => setWishes({ ...wishes, min_rooms: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" />
                <Input placeholder="bagni" type="number" value={wishes.min_bathrooms} onChange={(e) => setWishes({ ...wishes, min_bathrooms: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 h-9 font-mono-data" />
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div>
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">DOTAZIONI</Label>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              {[["elevator", "Ascensore"], ["terrace", "Terrazzo"], ["garden", "Giardino"], ["garage", "Box auto"], ["furnished", "Arredato"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={wishes[k]} onCheckedChange={(v) => setWishes({ ...wishes, [k]: v })} data-testid={`wish-${k}`} />
                  <span className="text-zinc-300">{l}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Categorical selects */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CLASSE ≥</Label>
              <Select value={wishes.energy_class || "_"} onValueChange={(v) => setWishes({ ...wishes, energy_class: v === "_" ? "" : v })}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="_">Nessuna</SelectItem>
                  {ENERGY_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">ANNO COSTR. ≥</Label>
              <Input type="number" placeholder="1980" value={wishes.min_year_built} onChange={(e) => setWishes({ ...wishes, min_year_built: e.target.value })} className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9 font-mono-data" />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CONDIZIONI</Label>
              <Select value={wishes.condition || "_"} onValueChange={(v) => setWishes({ ...wishes, condition: v === "_" ? "" : v })}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="_">Qualsiasi</SelectItem>
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">RISCALDAMENTO</Label>
              <Select value={wishes.heating || "_"} onValueChange={(v) => setWishes({ ...wishes, heating: v === "_" ? "" : v })}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="_">Qualsiasi</SelectItem>
                  {HEATING.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">ESPOSIZIONE</Label>
              <Select value={wishes.exposure || "_"} onValueChange={(v) => setWishes({ ...wishes, exposure: v === "_" ? "" : v })}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="_">Qualsiasi</SelectItem>
                  {EXPOSURES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Zones */}
          <div>
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">QUARTIERI PREFERITI</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {zones.map((z) => (
                <button
                  key={z}
                  type="button"
                  onClick={() => toggleZone(z)}
                  className={`px-2 py-1 text-xs font-mono-data transition-colors border ${
                    wishes.zones.includes(z)
                      ? "bg-lime-400 text-lime-950 border-lime-400"
                      : "bg-zinc-900 text-zinc-400 border-white/10 hover:bg-zinc-800"
                  }`}
                  data-testid={`wish-zone-${z}`}
                >
                  {z}
                </button>
              ))}
            </div>
          </div>

          {/* Sources */}
          <div>
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">FONTI DATI</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {sources.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSource(s.id)}
                  className={`px-2 py-1 text-xs font-mono-data transition-colors border ${
                    wishes.sources.includes(s.id)
                      ? "bg-cyan-500 text-cyan-950 border-cyan-500"
                      : "bg-zinc-900 text-zinc-400 border-white/10 hover:bg-zinc-800"
                  }`}
                  data-testid={`wish-source-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-white/10 grid grid-cols-3 gap-2">
          <Button variant="outline" className="rounded-none border-white/20 col-span-1" onClick={reset} data-testid="btn-reset">Reset</Button>
          <Button onClick={submit} disabled={loading} className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 col-span-2 font-bold" data-testid="btn-ricerca-submit">
            {loading ? "Ricerca..." : "Trova immobili →"}
          </Button>
        </div>
      </aside>

      {/* Results */}
      <main className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <Sparkle size={18} weight="bold" className="text-lime-400" />
          <h2 className="font-display font-black text-2xl">Risultati</h2>
          <span className="font-mono-data text-xs text-zinc-500 ml-auto" data-testid="ricerca-count">
            {results.length} di {total} match
          </span>
        </div>
        {results.length === 0 ? (
          <div className="border border-dashed border-white/10 p-16 text-center">
            <div className="font-mono-data text-xs text-zinc-500">
              Compila i criteri e premi <span className="text-lime-400">"Trova immobili"</span>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="ricerca-results">
            {results.map((r, idx) => (
              <div key={r.id} className="border border-white/10 bg-zinc-900/60 p-4 hover:border-lime-400/50 transition-colors" data-testid={`result-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-data text-2xl font-black text-zinc-700">#{idx + 1}</span>
                    <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-xs font-bold hover:bg-lime-400">
                      {r.match_score}% MATCH
                    </Badge>
                  </div>
                  <Badge className="rounded-none bg-zinc-800 text-zinc-300 font-mono-data text-[10px] hover:bg-zinc-800">{r.source}</Badge>
                </div>
                <div className="font-display font-bold text-base leading-tight mb-1">{r.title}</div>
                <div className="text-xs text-zinc-500 mb-2">{r.address}</div>
                <div className="font-mono-data text-xl font-bold text-lime-400 mb-3">
                  {formatEuro(r.price)}{r.type === "affitto" && <span className="text-xs text-zinc-500">/mese</span>}
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {r.match_reasons.slice(0, 4).map((reason) => (
                    <span key={reason} className="text-[10px] font-mono-data bg-lime-400/10 text-lime-400 border border-lime-400/30 px-1.5 py-0.5 flex items-center gap-1">
                      <CheckCircle size={9} weight="bold" />{reason}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-1 pt-3 border-t border-white/5 font-mono-data text-xs text-zinc-400">
                  <div><div className="text-[10px] text-zinc-600">M²</div>{r.sqm}</div>
                  <div><div className="text-[10px] text-zinc-600">LOCALI</div>{r.rooms}</div>
                  <div><div className="text-[10px] text-zinc-600">BAGNI</div>{r.bathrooms}</div>
                  <div><div className="text-[10px] text-zinc-600">CLASSE</div>{r.energy_class}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <WatchButton kind="listing" id={r.id} city={r.city} />
                  <PdfButton kind="property" id={r.id} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
